"use client";

import { useState, useEffect, useRef } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Play, Pause } from 'lucide-react';
export default function Hero() {
  const {
    ref: markerRef,
    isVisible: markerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: labelRef,
    isVisible: labelVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const {
    ref: serifRef,
    isVisible: serifVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.1
  });
  const {
    ref: stripRef,
    isVisible: stripVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [videoURL, setVideoURL] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const rotatingWords = ["Herkenbaarheid", "Groei", "Consistentie"];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWordIndex(prev => (prev + 1) % rotatingWords.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [rotatingWords.length]);
  useEffect(() => {
    const savedVideo = localStorage.getItem("heroVideoURL");
    if (savedVideo) {
      setVideoURL(savedVideo);
    }
  }, []);
  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("video/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setVideoURL(base64String);
        localStorage.setItem("heroVideoURL", base64String);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleVideoClick = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };
  return <section data-section-id="3313" id="herkenbaar-groeien" className="relative text-white min-h-[560px] pt-32 pb-16 px-6 overflow-hidden !bg-[rgba(25,25,25,1)] md:pt-44 lg:pt-[200px] md:pb-20 md:px-10 lg:px-16">
      <div className="absolute inset-0 z-0 opacity-30 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723703/img/hero_background_digital.png)]" aria-hidden="true" />
      <div className="absolute inset-0 z-0 from-[hsl(0,0%,10%)] via-transparent to-[hsl(0,0%,10%)] !bg-[rgba(25,25,25,1)]" aria-hidden="true" />

      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-px z-10" style={{
      background: "linear-gradient(to bottom, transparent 0%, hsla(0, 0%, 100%, 0.1) 50%, transparent 100%)"
    }}>
        <motion.div animate={{
        top: ["0%", "100%"],
        opacity: [0, 1, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: "linear"
      }} className="absolute left-0 w-px h-32 bg-white" />
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-8 md:left-16 w-px z-10" style={{
      background: "linear-gradient(to bottom, transparent 10%, hsla(30, 100%, 50%, 0.1) 50%, transparent 90%)"
    }}>
        <motion.div animate={{
        top: ["100%", "0%"],
        opacity: [0, 1, 0]
      }} transition={{
        duration: 4,
        repeat: Infinity,
        ease: "linear"
      }} className="absolute left-0 w-px h-32 bg-[hsl(30,100%,50%)]" />
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-px z-10" style={{
      background: "linear-gradient(to bottom, transparent 0%, hsla(0, 0%, 100%, 0.1) 50%, transparent 100%)"
    }}>
        <motion.div animate={{
        top: ["0%", "100%"],
        opacity: [0, 1, 0]
      }} transition={{
        duration: 3.5,
        repeat: Infinity,
        ease: "linear"
      }} className="absolute left-0 w-px h-32 bg-white" />
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-8 md:right-16 w-px z-10" style={{
      background: "linear-gradient(to bottom, transparent 10%, hsla(30, 100%, 50%, 0.1) 50%, transparent 90%)"
    }}>
        <motion.div animate={{
        top: ["100%", "0%"],
        opacity: [0, 1, 0]
      }} transition={{
        duration: 4.5,
        repeat: Infinity,
        ease: "linear"
      }} className="absolute left-0 w-px h-32 bg-[hsl(30,100%,50%)]" />
      </div>

      <div className="relative z-20 mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
          <div ref={labelRef} className={`transition-all duration-700 ease-out ${labelVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
            
          </div>

          <motion.h1 ref={headlineRef} initial={{
          opacity: 0,
          y: 20,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          y: 0,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1,
          ease: "easeOut"
        }} className="font-default font-extrabold text-white mt-8 md:mt-10 leading-[0.9] tracking-tighter text-[12vw] sm:text-[11vw] md:text-[11vw] lg:text-[90px] xl:text-[115px]" style={{
          fontWeight: 800
        }}>
            <span className="flex flex-col items-center lg:items-center gap-0 w-full">
              <span className="inline-flex relative w-full overflow-hidden h-[1.3em] -mb-3 px-0 text-[hsl(30,100%,50%)] min-w-[280px] md:min-w-[400px] lg:min-w-[560px]">
                <AnimatePresence mode="wait">
                 const rotatingWords = ["Consistentie", "Herkenbaarheid", "Groei"];

                <div className="relative w-full h-[1.2em] overflow-hidden">
  <AnimatePresence mode="wait">
    <motion.span key={rotatingWords[currentWordIndex]} initial={{
                      opacity: 0,
                      y: 40
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: -40
                    }} transition={{
                      duration: 0.4,
                      ease: "easeInOut"
                    }} className="absolute inset-0 flex items-center justify-center whitespace-nowrap ![letter-spacing:-5%] !text-[40px] lg:!text-[64px]">
      {rotatingWords[currentWordIndex]}
    </motion.span>
  </AnimatePresence>
                </div>
                </AnimatePresence>
              </span>
              <span className="w-full text-center ![letter-spacing:0.1%] !text-[45px]">Begint hier.</span>
            </span>
            
          </motion.h1>

          <div className="w-24 h-1 bg-[hsl(30,100%,50%)] mt-8 mb-4 rounded-lg" />

          <p className="font-mono text-[hsl(30,100%,50%)] text-sm md:text-base uppercase tracking-widest mb-6">
            Maximale Zichtbaarheid. Meetbare Groei.
          </p>

          <p ref={serifRef} className={`font-default text-white/70 text-base md:text-lg lg:text-xl leading-relaxed max-w-[580px] transition-all duration-700 delay-150 ease-out ${serifVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>Wij bouwen merken die opvallen en resultaten behalen. Met strategie, content en social media creëren we blijvende impact.</p>
        </div>

        <div className="flex flex-col items-center justify-center w-full">
          <div className="relative inline-flex w-full max-w-[340px] md:max-w-[380px] p-0 bg-[hsl(0,0%,13%)] border-2 border-[hsl(30,100%,50%)]/60 rounded-lg shadow-2xl items-center justify-center overflow-hidden">
            <video ref={videoRef} controls autoPlay loop playsInline preload="auto" onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} className="w-full h-auto object-cover rounded-lg mx-auto">
              <source src={videoURL || "https://outgoing-horse.10web.cloud/wp-content/uploads/2026/07/DM_groei_SC_2.mp4"} type="video/mp4" />
            </video>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Button onClick={handleVideoClick} className="pointer-events-auto opacity-0 bg-[hsl(30,100%,50%)] text-[hsl(0,0%,10%)] hover:bg-[hsl(30,100%,45%)] size-12 rounded-lg shadow-lg transition-transform hover:scale-110">
                {isPlaying ? <Pause className="size-6" /> : <Play className="size-6 ml-0.5" />}
              </Button>
            </div>
            <input type="file" accept="video/*" ref={fileInputRef} onChange={handleVideoChange} className="hidden" />
          </div>
          
          <Button asChild className="w-full max-w-[380px] mt-6 bg-[hsl(30,100%,50%)] text-[hsl(0,0%,10%)] hover:bg-[hsl(30,100%,45%)] font-bold uppercase tracking-wider py-8 rounded-lg shadow-lg transition-all duration-300 hover:-translate-y-1">
            <Link to="#contact" onClick={e => {
            e.preventDefault();
            const contactSection = document.getElementById('contact');
            contactSection?.scrollIntoView({
              behavior: 'smooth'
            });
            setTimeout(() => {
              const firstInput = contactSection?.querySelector('input, textarea, select') as HTMLElement;
              firstInput?.focus();
            }, 300);
          }}>
              GROEI!
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}